import { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Input } from '@/components/ui/input';
import { LogIn, UserPlus, Loader2 } from 'lucide-react';
import api from '@/lib/api';

export default function Login() {
  const [isRegister, setIsRegister] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [azureLoading, setAzureLoading] = useState(false);
  const [azureAdEnabled, setAzureAdEnabled] = useState(false);
  const [registrationEnabled, setRegistrationEnabled] = useState(false);
  const [passwordLoginEnabled, setPasswordLoginEnabled] = useState(false);
  const [configLoading, setConfigLoading] = useState(true);
  const { login, register } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // Check for error params from Azure AD callback
  useEffect(() => {
    const errorParam = searchParams.get('error');
    if (errorParam) {
      const errorMessages = {
        'azure_not_configured': 'Azure AD is not configured',
        'token_exchange_failed': 'Failed to authenticate with Microsoft',
        'user_info_failed': 'Failed to get user information',
        'no_email': 'No email address found in Microsoft account',
        'internal_error': 'An internal error occurred',
        'access_denied': 'Access was denied',
        'account_link_refused': 'This email already has a password account. Sign in with your password, or ask the Speedforce admin to allow Microsoft sign-in for your account.',
        'account_link_needs_admin': 'Ask an admin to allow Microsoft sign-in for your account.',
        'invalid_state': 'The sign-in session expired or did not match. Please try again.',
        'account_disabled': 'This account is deactivated. Contact an administrator.'
      };
      setError(errorMessages[errorParam] || `Authentication error: ${errorParam}`);
    }
  }, [searchParams]);

  // Fetch auth config to check if Azure AD is enabled
  useEffect(() => {
    const fetchConfig = async () => {
      try {
        const res = await api.get('/auth/config');
        setAzureAdEnabled(res.data.azure_ad_enabled);
        setPasswordLoginEnabled(Boolean(res.data.password_login_enabled));
        setRegistrationEnabled(Boolean(res.data.password_login_enabled && res.data.registration_enabled));
      } catch (err) {
        console.error('Failed to fetch auth config:', err);
        setError('Could not load the sign-in options. Refresh the page to try again.');
      } finally {
        setConfigLoading(false);
      }
    };
    fetchConfig();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      if (isRegister) {
        await register(email, password, name);
      } else {
        await login(email, password);
      }
      navigate('/');
    } catch (err) {
      const detail = err.response?.data?.detail;
      // 422 validation errors arrive as a list of { msg } objects.
      setError(Array.isArray(detail) ? detail.map((d) => d.msg).join(' ') : detail || 'Something went wrong');
    } finally {
      setLoading(false);
    }
  };

  // Navigate (not XHR) to the API, which sets a signed sign-in cookie on its
  // own origin and redirects to Microsoft with state and PKCE.
  const handleAzureLogin = () => {
    setError('');
    setAzureLoading(true);
    const redirectOrigin = encodeURIComponent(window.location.origin);
    window.location.assign(`${process.env.REACT_APP_BACKEND_URL}/api/auth/azure/login?redirect_origin=${redirectOrigin}`);
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#faf8fb] px-4" data-testid="login-page">
      <div className="w-full max-w-sm">
        {/* Logo */}
        <div className="flex flex-col items-center mb-10">
          <img src="/advania-logo.png" alt="Advania" className="h-12 w-auto mb-3" />
          <div className="flex items-center gap-2">
            <span className="text-[11px] uppercase tracking-[0.9px] font-bold text-[#98139C]">Speedforce</span>
            <span className="text-[11px] text-[#30302F]">|</span>
            <span className="text-[11px] text-[#30302F] font-light">PM Co-Pilot</span>
          </div>
        </div>

        {/* Form card */}
        <div className="bg-white border border-[#e3e3e3] p-8">
          <h2 className="text-2xl font-heading text-[#30302F] mb-1 text-center">
            {isRegister ? 'Create your account' : 'Sign in'}
          </h2>
          <p className="text-[#30302F] mb-6 text-sm font-light text-center">
            {isRegister ? 'Get started with Speedforce' : 'Welcome back to your workspace'}
          </p>

          {error && (
            <div data-testid="login-error" className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 mb-4 text-sm">
              {error}
            </div>
          )}

          {/* Azure AD SSO Button - only shown when configured */}
          {!isRegister && azureAdEnabled && !configLoading && (
            <>
              <button
                type="button"
                onClick={handleAzureLogin}
                disabled={azureLoading}
                data-testid="azure-login-button"
                className="w-full h-11 border border-[#e3e3e3] bg-white hover:bg-gray-50 text-[#30302F] rounded-full font-medium text-sm transition-colors flex items-center justify-center gap-3 mb-4"
              >
                {azureLoading ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <>
                    <svg className="w-5 h-5" viewBox="0 0 21 21" fill="none">
                      <rect x="1" y="1" width="9" height="9" fill="#F25022"/>
                      <rect x="11" y="1" width="9" height="9" fill="#7FBA00"/>
                      <rect x="1" y="11" width="9" height="9" fill="#00A4EF"/>
                      <rect x="11" y="11" width="9" height="9" fill="#FFB900"/>
                    </svg>
                    Sign in with Microsoft
                  </>
                )}
              </button>

              {passwordLoginEnabled && (
              <div className="relative my-5">
                <div className="absolute inset-0 flex items-center">
                  <span className="w-full border-t border-[#e3e3e3]" />
                </div>
                <div className="relative flex justify-center text-xs">
                  <span className="bg-white px-3 text-[#30302F]/70">or continue with email</span>
                </div>
              </div>
              )}
            </>
          )}

          {!configLoading && !passwordLoginEnabled && !azureAdEnabled && (
            <p data-testid="login-unavailable" className="text-sm text-[#30302F] text-center">
              Sign-in is not available. Contact an administrator.
            </p>
          )}

          {passwordLoginEnabled && !configLoading && (
          <form onSubmit={handleSubmit} className="space-y-4">
            {isRegister && (
              <>
                <div>
                  <label className="block text-[11px] uppercase tracking-[0.9px] font-bold text-[#30302F] mb-1.5">Full Name</label>
                  <Input data-testid="register-name-input" value={name} onChange={(e) => setName(e.target.value)} placeholder="Your name" required className="rounded-none border-[#e3e3e3] focus-visible:ring-[#98139C]" />
                </div>
              </>
            )}
            <div>
              <label className="block text-[11px] uppercase tracking-[0.9px] font-bold text-[#30302F] mb-1.5">Email</label>
              <Input data-testid="login-email-input" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@company.com" required className="rounded-none border-[#e3e3e3] focus-visible:ring-[#98139C]" />
            </div>
            <div>
              <label className="block text-[11px] uppercase tracking-[0.9px] font-bold text-[#30302F] mb-1.5">Password</label>
              <Input data-testid="login-password-input" type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Enter your password" required className="rounded-none border-[#e3e3e3] focus-visible:ring-[#98139C]" />
            </div>
            <button data-testid="login-submit-button" type="submit" disabled={loading} className="w-full bg-[#98139C] hover:bg-[#7a0f7d] text-white h-11 rounded-full font-bold text-sm transition-colors flex items-center justify-center gap-2">
              {loading ? (
                <span className="flex items-center gap-2">
                  <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  {isRegister ? 'Creating account...' : 'Signing in...'}
                </span>
              ) : (
                <span className="flex items-center gap-2">
                  {isRegister ? <UserPlus size={16} /> : <LogIn size={16} />}
                  {isRegister ? 'Create Account' : 'Sign In'}
                </span>
              )}
            </button>
          </form>
          )}

          {passwordLoginEnabled && (registrationEnabled || isRegister) && (
          <div className="mt-5 text-center">
            <button
              data-testid="toggle-auth-mode"
              type="button"
              onClick={() => { setIsRegister(!isRegister); setError(''); }}
              className="text-sm text-[#98139C] hover:underline font-medium"
            >
              {isRegister ? 'Already have an account? Sign in' : "Don't have an account? Create one"}
            </button>
          </div>
          )}
        </div>

        <p className="text-center text-[11px] text-[#30302F] mt-6 font-light">
          Powered by Advania
        </p>
      </div>
    </div>
  );
}
